package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.TypeAst
import top.wcpe.allincore.dsl.compiler.lex.TokenType
import top.wcpe.allincore.dsl.compiler.source.SourcePosition

/**
 * M5-c / M5-d：类型标注解析器。
 *
 * 语法：
 *
 * ```
 * Type        ::= AtomType '?'?
 * AtomType    ::= IDENT ( '<' Type (',' Type)* '>' )?
 *               | KW_STRING | KW_NUMBER | KW_BOOL | KW_COLOR
 *               | FunctionType
 *               | '(' Type ')'                              // 括号包裹
 * FunctionType ::= 'suspend'? '(' (Type (',' Type)*)? ')' '->' Type
 * ```
 *
 * 调用点须已消费 `:`，保证下一个 token 是类型起始位置。
 */
internal object TypeParser {

    /** `suspend` 是软关键字，在词法层为 IDENTIFIER。 */
    private const val SOFT_KW_SUSPEND = "suspend"

    /** 解析一个完整类型（含可选 `?`）。 */
    fun parse(p: Parser): TypeAst {
        val atom = parseAtom(p)
        if (p.check(TokenType.QUESTION)) {
            p.advance()
            return markNullable(atom)
        }
        return atom
    }

    /** 把类型节点标记为可空；不同子类拷贝形态不同。 */
    private fun markNullable(t: TypeAst): TypeAst = when (t) {
        is TypeAst.Named -> t.copy(nullable = true)
        is TypeAst.Generic -> t.copy(nullable = true)
        is TypeAst.Function -> t.copy(nullable = true)
    }

    /**
     * 解析原子类型：
     *  - `suspend (...) -> R` → suspend 函数类型
     *  - `(...) -> R` → 函数类型
     *  - `( Type )` → 括号包裹（用于 `((T) -> R)?` 这种可空函数类型）
     *  - IDENT / 内置类型关键字，允许尾随 `<...>` 泛型实参
     */
    private fun parseAtom(p: Parser): TypeAst {
        // suspend 函数类型：`suspend (` 前瞻
        if (p.check(TokenType.IDENTIFIER) && p.current().lexeme == SOFT_KW_SUSPEND &&
            p.peek(1).type == TokenType.LPAREN
        ) {
            val startTok = p.current()
            p.advance() // 消费 suspend
            return parseFunctionTypeFromLParen(p, isSuspend = true, startPos = startTok.position)
        }
        // `(` 开头：可能是函数类型，也可能是括号包裹的类型
        if (p.check(TokenType.LPAREN)) {
            if (lookaheadIsFunctionType(p)) {
                val startTok = p.current()
                return parseFunctionTypeFromLParen(p, isSuspend = false, startPos = startTok.position)
            }
            // 括号包裹：`( Type )`，常用于可空函数类型 `((T) -> R)?` 外层
            p.advance() // 消费 '('
            val inner = parse(p)
            p.expect(TokenType.RPAREN, "类型括号缺少 ')'")
            return inner
        }
        val tok = p.current()
        val name = when (tok.type) {
            TokenType.IDENTIFIER -> tok.lexeme
            TokenType.KW_STRING -> "String"
            TokenType.KW_NUMBER -> "Number"
            TokenType.KW_BOOL -> "Boolean"
            TokenType.KW_COLOR -> "Color"
            else -> p.fail("类型标注起始位置期望类型名，实际 ${tok.type}（'${tok.lexeme}'）", tok.position)
        }
        p.advance()
        // 泛型实参 `<T, U>`
        if (p.check(TokenType.LT)) {
            p.advance()
            val args = mutableListOf<TypeAst>()
            args += parse(p)
            while (p.check(TokenType.COMMA)) {
                p.advance()
                p.skipNewlines()
                args += parse(p)
            }
            p.expect(TokenType.GT, "泛型实参缺少 '>'")
            return TypeAst.Generic(name, args, nullable = false, position = tok.position)
        }
        return TypeAst.Named(name, nullable = false, position = tok.position)
    }

    /**
     * 从当前 `(` 起解析函数类型 `(T1, T2) -> R`。
     *
     * 调用前要求当前 token 为 `LPAREN`；`startPos` 用于结果节点的位置标记
     * （suspend 函数类型应使用 `suspend` 关键字位置，普通函数类型用 `(` 位置）。
     */
    private fun parseFunctionTypeFromLParen(
        p: Parser,
        isSuspend: Boolean,
        startPos: SourcePosition,
    ): TypeAst {
        p.expect(TokenType.LPAREN, "函数类型缺少 '('")
        val params = mutableListOf<TypeAst>()
        if (!p.check(TokenType.RPAREN)) {
            params += parse(p)
            while (p.check(TokenType.COMMA)) {
                p.advance()
                p.skipNewlines()
                params += parse(p)
            }
        }
        p.expect(TokenType.RPAREN, "函数类型参数列表缺少 ')'")
        p.expect(TokenType.ARROW, "函数类型缺少 '->'")
        val ret = parse(p)
        return TypeAst.Function(
            params = params,
            ret = ret,
            isSuspend = isSuspend,
            nullable = false,
            position = startPos,
        )
    }

    /**
     * 前瞻判断从当前 `(` 起是否为函数类型。
     *
     * 通过括号配平扫描到匹配的 `)`，然后检查其后（跳过 NEWLINE）是否为 `->`。
     * 不会移动解析器游标。
     */
    private fun lookaheadIsFunctionType(p: Parser): Boolean {
        // 当前 token 应为 LPAREN
        var depth = 0
        var i = 0
        while (true) {
            val t = p.peek(i)
            when (t.type) {
                TokenType.LPAREN -> depth++
                TokenType.RPAREN -> {
                    depth--
                    if (depth == 0) {
                        // 跳过 RPAREN 之后的 NEWLINE，查看是否紧跟 ARROW
                        var j = i + 1
                        while (p.peek(j).type == TokenType.NEWLINE) j++
                        return p.peek(j).type == TokenType.ARROW
                    }
                }
                TokenType.EOF -> return false
                else -> Unit
            }
            i++
        }
    }
}
